import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import { t } from 'i18next';
import { FormApi } from 'final-form';
import { Button } from 'ncoded-component-library';
import { Form, Field } from 'react-final-form';
import SideDrawer from '../../../SideDrawer';
import RadioGroupField from 'components/RadioGroupField';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import TextAreaField from 'components/TextAreaField';
import useHeightAnimation from 'hooks/useHeightAnimation';
import useCallbackRef from 'hooks/useCallbackRef';
import TrashIcon from 'icons/Trash.icon';
import QAndAContext from 'router/subrouters/DataRoom/providers/QAndA/QAndA.context';
import validations from 'validations';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { QuestionPreview } from '../../../QuestionPreview';
import useGetFollowUpQuestion from '../../hooks/useGetFollowUpQuestion';
import {
  Q_AND_A_SIDES,
  QuestionManagingDrawerCommonProps,
} from 'router/subrouters/DataRoom/models/QAndA';
import { PreviewSideDrawerRef } from '../../../PreviewQuestionDrawer/PreviewQuestionDrawer.component';
import { getUserFullName } from 'utils';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  ANSWER_REVIEW_OUTPUT,
  APPROVAL_MODUS,
  QuestionReviewInitiatedEventProps,
} from 'models/MixpanelEvents';
import { richTextJsonToPlainText } from 'components/RichTextEditorField/utils';

import './ManageQuestion.styles.scss';

type ManageQuestionDrawerProps = QuestionManagingDrawerCommonProps;

type ManageQuestionDrawerForm = {
  respond?: string;
  action: 'approveQuestion' | 'rejectQuestion';
};

const ManageQuestionDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageQuestionDrawerProps
> = ({ question, onClose, onQuestionsUpdate, updateQuestion }, ref) => {
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();
  const { isQuestionDeletableForUser, deleteQuestionCallback } =
    useContext(QAndAContext);

  const [drawer, drawerRef] = useCallbackRef<PreviewSideDrawerRef>();

  useImperativeHandle(ref, () => drawer, [drawer]);

  const getMixpanelProps = useGetMixpanelProps();

  const { isLoading, displayedQuestions } = useGetFollowUpQuestion(
    question,
    drawer?.open,
  );

  useHeightAnimation({
    element: container,
    defaultOpen: true,
  });

  const onQuestionManage = useCallback(
    async (
      values: ManageQuestionDrawerForm,
      formApi: FormApi<
        ManageQuestionDrawerForm,
        Partial<ManageQuestionDrawerForm>
      >,
    ) => {
      try {
        mixpanelService.questionReviewInitiated(
          getMixpanelProps({
            questionId: question._id,
            questionContent: richTextJsonToPlainText(question.content),
            approvalModus: APPROVAL_MODUS.MANUAL,
            response: values.respond,
            questionOutput:
              values.action === 'approveQuestion'
                ? ANSWER_REVIEW_OUTPUT.ACCEPTED
                : ANSWER_REVIEW_OUTPUT.REJECTED,
            isOwner: false,
            userSide: Q_AND_A_SIDES.BUY,
          }) as QuestionReviewInitiatedEventProps,
        );

        await dataRoomApi.questionsAndAnswers[`${values.action}`](
          question._id,
          values.respond,
        );
        onQuestionsUpdate?.();
        onClose?.();
        popSuccess(t(`manageQuestionBuyOptions.${values.action}Success`));
        formApi.restart();
      } catch (e) {
        popServerError(e, t('qaError'));
      }
    },
    [
      question._id,
      question.content,
      getMixpanelProps,
      onQuestionsUpdate,
      onClose,
      popSuccess,
      popServerError,
    ],
  );

  const actionOptions = useMemo(
    () => [
      {
        label: t('manageQuestionBuyOptions.approveQuestion'),
        value: 'approveQuestion',
      },

      {
        label: t('manageQuestionBuyOptions.rejectQuestion', {
          name: getUserFullName(
            question.user?.firstName,
            question.user?.lastName,
          ),
        }),
        value: 'rejectQuestion',
      },
    ],
    [question],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      closeAction={onClose}
      isLoading={isLoading}
      className="qa-manage-question-buy"
    >
      <p className="drawer-title">{t('manageQuestion')}</p>

      <Form
        onSubmit={onQuestionManage}
        initialValues={{ action: 'approveQuestion' }}
        render={({ handleSubmit, submitting, invalid, values }) => {
          const action = values.action;

          return (
            <form
              onSubmit={handleSubmit}
              className="qa-manage-question-buy__form"
            >
              <QuestionPreview
                question={question}
                displayedQuestions={displayedQuestions}
                updateQuestion={updateQuestion}
              />

              <RadioGroupField
                name="action"
                direction="column"
                className="qa-manage-question-buy__action"
                options={actionOptions}
              />

              <div className="textarea-container" ref={containerRef}>
                {action === 'rejectQuestion' && (
                  <Field
                    label={t('respondToQuestion')}
                    className="qa-manage-question-buy__respond"
                    name="respond"
                    component={TextAreaField}
                    maxLength={2000}
                    required
                    validate={validations.required()}
                  />
                )}
              </div>

              <div className="qa-manage-question-buy__form__actions">
                <Button type="submit" disabled={submitting || invalid}>
                  {t(`manageQuestionBuyOptions.${action}Text`)}
                </Button>
                {isQuestionDeletableForUser(question) && (
                  <Button
                    className="delete"
                    disabled={submitting}
                    icon={TrashIcon}
                    onClick={async (ev) => {
                      (ev.target as any).classList.add('loading');
                      await deleteQuestionCallback(question);
                      onQuestionsUpdate?.();
                      onClose?.();
                      (ev.target as any).classList.remove('loading');
                    }}
                  >
                    {t('deleteQuestion')}
                  </Button>
                )}
              </div>
            </form>
          );
        }}
      />
    </SideDrawer>
  );
};

export default forwardRef(ManageQuestionDrawer);
